<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
            <small class="text-muted">入库时拍摄的商品·包装·尺寸照片，按商品(货主+条码)汇总(입고 때 찍은 사진을 상품별로 모아 봅니다)</small>
          </div>
          <div class="card-body">
            <!-- 사진 종류 -->
            <div class="btn-group btn-group-sm mb-3">
              <button v-for="k in kinds" :key="k.value" type="button"
                      :class="['btn', kind === k.value ? 'btn-primary' : 'btn-outline-primary']"
                      @click="changeKind(k.value)">{{ k.text }}</button>
            </div>

            <!-- 확인 여부 (종류 필터와 함께 적용) -->
            <div class="btn-group btn-group-sm mb-3 ml-2">
              <button v-for="c in confirmFilters" :key="c.value" type="button"
                      :class="['btn', confirmed === c.value ? 'btn-success' : 'btn-outline-success']"
                      @click="changeConfirmed(c.value)">{{ c.text }}</button>
            </div>

            <!-- 검색 -->
            <div class="d-flex flex-wrap align-items-center mb-3 photo-lib__filters">
              <input type="text" v-model.trim="keyword" placeholder="韩文品名(상품명) / 订单号(주문번호)" class="form-control form-control-sm"
                     style="width:270px" @keyup.enter="search(1)">
              <input type="text" v-model.trim="barcode" placeholder="条码号(바코드)" class="form-control form-control-sm"
                     style="width:170px" @keyup.enter="search(1)">
              <input type="text" v-model.trim="companyid" list="photoLibCompanyList" placeholder="货主(화주) 全部"
                     class="form-control form-control-sm" style="width:160px" @keyup.enter="search(1)">
              <datalist id="photoLibCompanyList">
                <option v-for="c in companyOptions" :key="c" :value="c"></option>
              </datalist>
              <label class="mb-0 small">入库日期(입고일)</label>
              <input type="date" v-model="startdate" class="form-control form-control-sm" style="width:150px">
              <span>~</span>
              <input type="date" v-model="enddate" class="form-control form-control-sm" style="width:150px">
              <b-button size="sm" variant="primary" :disabled="loading" @click="search(1)">搜索<small>(검색)</small></b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="reset">重置<small>(초기화)</small></b-button>
              <span class="small text-muted">共 {{ total }}个商品(상품)</span>
            </div>
            <div v-if="truncated" class="small text-warning mb-2">
              结果过多，仅统计最近3000条入库记录，请缩小条件(결과가 많아 최근 입고 3000건만 묶었습니다. 조건을 좁혀주세요)
            </div>

            <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>
            <div v-else-if="!items.length" class="text-center text-muted py-5">没有照片(사진이 없습니다)</div>

            <div v-else class="photo-lib__grid">
              <div v-for="g in items" :key="g.key" class="photo-lib__card">
                <div class="photo-lib__head">
                  <img v-if="g.이미지" :src="g.이미지" class="photo-lib__thumb-main" alt="">
                  <div class="photo-lib__info">
                    <div class="photo-lib__name" :title="g.상품명">{{ g.상품명 || '-' }}</div>
                    <div v-if="g.옵션" class="small text-muted text-truncate" :title="g.옵션">{{ g.옵션 }}</div>
                    <div class="small">条码(바코드) <b>{{ g.바코드 || '-' }}</b></div>
                    <div class="small">货主(화주) <b>{{ g.companyid }}</b></div>
                    <div v-if="orderNosOfGroup(g).length" class="small" :title="orderNosOfGroup(g).join(', ')">订单号(주문번호)
                      <b>{{ orderNosOfGroup(g).slice(0, 2).join(', ') }}</b><span v-if="orderNosOfGroup(g).length > 2"> 外 {{ orderNosOfGroup(g).length - 2 }}</span>
                    </div>
                    <div class="small text-muted">最近入库(최근 입고) {{ ymd(g.최근입고일) }} · {{ g.입고건수 }}次(회)</div>
                  </div>
                </div>
                <div class="photo-lib__size">
                  <template v-if="g.size">
                    📏 {{ g.size.sizeX }}×{{ g.size.sizeY }}×{{ g.size.sizeZ }}cm · {{ g.size.weightKg }}kg
                  </template>
                  <span v-else class="text-muted">📏 无尺寸记录(사이즈 없음)</span>
                </div>
                <div v-if="g.바코드 && g.companyid" class="mb-2">
                  <b-button size="sm" :variant="g.확인 && g.확인.confirmed ? 'success' : 'outline-secondary'"
                            :disabled="!!confirming[g.key]" :title="confirmTip(g)" @click="toggleConfirm(g)">
                    <b-spinner v-if="confirming[g.key]" small class="mr-1"></b-spinner>
                    <template v-if="g.확인 && g.확인.confirmed">✔ 已确认<small>(확인됨)</small></template>
                    <template v-else>已确认<small>(확인 표시)</small></template>
                  </b-button>
                  <span v-if="g.확인 && g.확인.confirmed" class="small text-muted ml-2">{{ confirmTip(g) }}</span>
                </div>
                <div v-if="g.photos.length && g.바코드 && g.companyid" class="mb-2">
                  <b-button size="sm" variant="outline-primary" :disabled="!!zipping[g.key]" @click="downloadZip(g)">
                    <b-spinner v-if="zipping[g.key]" small class="mr-1"></b-spinner>下载全部照片(zip)<small>(사진 전체 받기)</small>
                  </b-button>
                </div>
                <div class="photo-lib__photos">
                  <div v-for="(p, pi) in g.photos.slice(0, PREVIEW)" :key="p.url" class="photo-lib__ph" @click="openViewer(g, pi)">
                    <img :src="p.url" alt="" loading="lazy">
                    <span :class="['photo-lib__badge', 'photo-lib__badge--' + kindCode(p.종류)]">{{ kindCn(p.종류) }}</span>
                  </div>
                  <div v-if="g.photos.length > PREVIEW" class="photo-lib__ph photo-lib__more" @click="openViewer(g, PREVIEW)">
                    +{{ g.photos.length - PREVIEW }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="total > pageSize" class="d-flex justify-content-center mt-3">
              <b-pagination v-model="page" :total-rows="total" :per-page="pageSize" size="sm" @change="search"></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 큰 사진 보기 -->
    <b-modal v-model="viewer.show" size="xl" hide-footer centered
             :title="viewer.group ? (viewer.group.상품명 + ' · ' + (viewer.group.바코드 || '')) : ''">
      <div v-if="viewerPhoto" class="photo-lib__viewer">
        <div class="mb-2 small">
          <span :class="['photo-lib__badge', 'photo-lib__badge--' + kindCode(viewerPhoto.종류)]">{{ kindCn(viewerPhoto.종류) }}</span>
          <span class="ml-2 text-muted">{{ ymd(viewerPhoto.at) }}</span>
          <span class="ml-2 text-muted">{{ viewer.index + 1 }} / {{ viewer.group.photos.length }}</span>
          <a :href="viewerPhoto.url" target="_blank" rel="noopener" class="ml-2">原图(원본)</a>
        </div>
        <div class="mb-2 small">
          <span>上传人(업로더): <b>{{ uploaderText(viewerPhoto) }}</b></span>
          <span v-if="(viewerPhoto.주문번호들 || []).length" class="ml-3">订单号(주문번호): <b>{{ viewerPhoto.주문번호들.join(', ') }}</b></span>
        </div>
        <div class="photo-lib__viewer-body">
          <b-button variant="light" class="photo-lib__nav" :disabled="viewer.index <= 0" @click="move(-1)">‹</b-button>
          <img :src="viewerPhoto.url" alt="" class="photo-lib__big">
          <b-button variant="light" class="photo-lib__nav" :disabled="viewer.index >= viewer.group.photos.length - 1" @click="move(1)">›</b-button>
        </div>
        <div class="photo-lib__strip">
          <img v-for="(p, pi) in viewer.group.photos" :key="'s' + p.url" :src="p.url" alt="" loading="lazy"
               :class="{ 'is-active': pi === viewer.index }" @click="viewer.index = pi">
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import axios from 'axios';

// 저장 키(value)는 그대로, 표기만 — ImportPhotoPanel 과 같은 이름
const KINDS = [
  { value: '입고사진', code: 'in', cn: '商品及包装' },
  { value: '사이즈사진', code: 'size', cn: '尺寸' },
  { value: '오류사진', code: 'err', cn: '错误' }
];

export default {
  name: 'LogisPhotoLibraryV0',
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.logisphotolibrary'),
      PREVIEW: 6,
      kinds: [
        { value: '', text: '全部(전체 — 오류사진 제외)' },
        { value: '입고사진', text: '商品及包装(상품·포장)' },
        { value: '사이즈사진', text: '尺寸(사이즈)' },
        { value: '오류사진', text: '错误(오류)' }
      ],
      kind: '',
      confirmFilters: [
        { value: '', text: '全部(전체)' },
        { value: 'yes', text: '已确认(확인됨)' },
        { value: 'no', text: '未确认(미확인)' }
      ],
      confirmed: '',
      confirming: {},
      keyword: '',
      barcode: '',
      companyid: '',
      startdate: '',
      enddate: '',
      companyOptions: [],
      items: [],
      total: 0,
      page: 1,
      pageSize: 24,
      truncated: false,
      loading: false,
      zipping: {},
      viewer: { show: false, group: null, index: 0 }
    };
  },
  computed: {
    viewerPhoto() {
      const g = this.viewer.group;
      return g && g.photos[this.viewer.index] ? g.photos[this.viewer.index] : null;
    }
  },
  mounted() {
    this.search(1);
  },
  methods: {
    kindCode(v) { const k = KINDS.find(x => x.value === v); return k ? k.code : 'in'; },
    kindCn(v) { const k = KINDS.find(x => x.value === v); return k ? k.cn : v; },
    // 카드 안 사진들의 주문번호를 합쳐 중복 없이(여러 주문이 섞인 카드)
    orderNosOfGroup(g) {
      const out = [];
      (g.photos || []).forEach(p => (p.주문번호들 || []).forEach(n => { if (!out.includes(n)) out.push(n); }));
      return out;
    },
    // 업로더 — 포장 QR 은 «pack:» 접두를 사람이 읽게, 기록이 없으면 «未记录»
    uploaderText(p) {
      const a = p && p.작업자;
      if (!a || (!a.계정 && !a.이름)) return '未记录(기록 없음)';
      const m = /^pack:(.*)$/i.exec(a.계정 || '');
      if (m) return '包装二维码(' + (m[1] || a.이름 || '') + ')';
      if (a.이름 && a.계정 && a.이름 !== a.계정) return a.이름 + ' (' + a.계정 + ')';
      return a.이름 || a.계정;
    },
    changeKind(v) {
      this.kind = v;
      this.search(1);
    },
    changeConfirmed(v) {
      this.confirmed = v;
      this.search(1);
    },
    confirmTip(g) {
      const c = g.확인;
      if (!c || !c.confirmed) return '点击标记为已确认(누르면 확인됨으로 표시)';
      const who = (c.by && (c.by.이름 || c.by.계정)) || '';
      return who + ' · ' + this.ymd(c.at);
    },
    // 누른 카드만 갱신한다(목록을 다시 불러오지 않는다). 이미 확인된 카드는 한 번 더 누르면 취소
    async toggleConfirm(g) {
      if (this.confirming[g.key]) return;
      this.$set(this.confirming, g.key, true);
      try {
        const { data } = await axios.post('/api/importPhoto/confirm', {
          companyid: g.companyid, barcode: g.바코드, confirmed: !(g.확인 && g.확인.confirmed)
        });
        if (data.요청결과 !== '성공') {
          alert('保存失败(저장 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        this.$set(g, '확인', data.확인);
      } catch (e) {
        console.error('사진 확인 표시 저장 오류:', e);
        alert('保存失败，请稍后再试(저장에 실패했습니다. 잠시 후 다시 시도해 주세요)');
      } finally {
        this.$set(this.confirming, g.key, false);
      }
    },
    reset() {
      this.keyword = '';
      this.barcode = '';
      this.companyid = '';
      this.startdate = '';
      this.enddate = '';
      this.kind = '';
      this.confirmed = '';
      this.search(1);
    },
    async search(page) {
      this.page = page || 1;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/importPhoto/library', {
          keyword: this.keyword, barcode: this.barcode, companyid: this.companyid, kind: this.kind, confirmed: this.confirmed,
          start: this.startdate, end: this.enddate, page: this.page, pageSize: this.pageSize
        });
        if (data.요청결과 !== '성공') {
          alert('搜索失败(검색 실패): ' + ((data.error && data.error.errorMessage) || ''));
          this.items = [];
          this.total = 0;
          return;
        }
        this.items = data.items || [];
        this.total = data.total || 0;
        this.truncated = !!data.truncated;
        const set = new Set(this.companyOptions);
        this.items.forEach(g => { if (g.companyid) set.add(g.companyid); });
        this.companyOptions = [...set].sort();
      } catch (e) {
        console.error('사진 라이브러리 검색 오류:', e);
        alert('搜索错误(검색 오류): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    // 서버가 만든 zip(파일명은 서버가 정함)을 받아 저장한다. 실패하면 서버가 JSON 으로 이유를 주거나 연결이 끊긴다
    async downloadZip(g) {
      if (this.zipping[g.key]) return;
      this.$set(this.zipping, g.key, true);
      try {
        const res = await axios.post('/api/importPhoto/zip',
          { companyid: g.companyid, barcode: g.바코드, kind: this.kind },
          { responseType: 'blob', timeout: 300000 });
        if (/json/i.test(res.data.type || '')) {
          console.error('사진 zip 서버 응답:', await res.data.text());
          alert('下载失败，请稍后再试(받기에 실패했습니다. 잠시 후 다시 시도해 주세요)');
          return;
        }
        let name = 'photos.zip';
        const cd = (res.headers && res.headers['content-disposition']) || '';
        const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
        if (m) { try { name = decodeURIComponent(m[1]); } catch (e) { console.error('zip 파일명 해석 오류:', e); } }
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        console.error('사진 zip 받기 오류:', e);
        alert('下载失败，请稍后再试(받기에 실패했습니다. 잠시 후 다시 시도해 주세요)');
      } finally {
        this.$set(this.zipping, g.key, false);
      }
    },
    openViewer(g, index) {
      this.viewer = { show: true, group: g, index: Math.min(index, g.photos.length - 1) };
    },
    move(step) {
      const n = this.viewer.index + step;
      if (n >= 0 && n < this.viewer.group.photos.length) this.viewer.index = n;
    },
    ymd(v) {
      if (!v) return '';
      const d = new Date(typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10) : v);
      if (isNaN(d.getTime())) return String(v).slice(0, 10);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }
  }
};
</script>

<style lang="scss" scoped>
.photo-lib {
  &__filters { gap: 8px; }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 12px;
  }
  &__card {
    border: 1px solid #e3e6ef;
    border-radius: 6px;
    padding: 10px;
    background: #fff;
  }
  &__head { display: flex; gap: 10px; }
  &__thumb-main {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }
  &__info { min-width: 0; flex: 1; }
  &__name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__size {
    margin: 6px 0;
    font-size: 13px;
  }
  &__photos {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
  }
  &__ph {
    position: relative;
    padding-top: 100%;
    cursor: pointer;
    background: #f4f5f9;
    border-radius: 4px;
    overflow: hidden;
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  &__more {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: #555;
    padding-top: 0;
    min-height: 60px;
  }
  &__badge {
    position: absolute;
    left: 2px;
    bottom: 2px;
    font-size: 11px;
    padding: 0 4px;
    border-radius: 3px;
    color: #fff;
    background: #3b6fd8;
    &--size { background: #1f9d6b; }
    &--err { background: #d64545; }
  }
  &__viewer .photo-lib__badge { position: static; }
  &__viewer-body {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  &__big {
    max-width: calc(100% - 100px);
    max-height: 70vh;
    object-fit: contain;
  }
  &__nav { font-size: 24px; }
  &__strip {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    margin-top: 8px;
    img {
      width: 60px;
      height: 60px;
      object-fit: cover;
      cursor: pointer;
      opacity: .6;
      border-radius: 3px;
      &.is-active { opacity: 1; outline: 2px solid #3b6fd8; }
    }
  }
}
</style>
